import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Loader2, SearchCode, ClipboardCopy, ChevronDown, ChevronRight, Search } from 'lucide-react';
import { toast } from 'sonner';
import { adminReceiptPaidNoCandidateAutopsyAPI } from '../../lib/adminReceiptPaidNoCandidateAutopsyApi';
import { getErrorMessage } from '../../lib/errors';
import { useJobPolling, JobProgressLine, JobErrorBlock } from './JobPollingPanel';

// Mirrors services/receipt_paid_no_candidate_autopsy.py::vendor_transaction_
// census's own literal `outcome` string values exactly -- never a second,
// independently-worded copy of what each outcome means.
const CENSUS_OUTCOME_LABELS = {
  found_in_window: 'Found in window',
  found_outside_window: 'Found outside window',
  not_in_feed_at_all: 'Not in feed at all',
  no_anchor_date: 'No anchor date',
};

function censusOutcomeLabel(outcome) {
  return CENSUS_OUTCOME_LABELS[outcome] || outcome;
}

function censusOutcomeVariant(outcome) {
  if (outcome === 'found_in_window') return 'default';
  if (outcome === 'not_in_feed_at_all') return 'destructive';
  return 'outline';
}

function VendorCensusPanel() {
  const [vendor, setVendor] = useState('');
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!vendor.trim()) return;
    setRunning(true);
    setError(null);
    try {
      const { data } = await adminReceiptPaidNoCandidateAutopsyAPI.vendorCensus(vendor.trim());
      setResult(data);
    } catch (err) {
      setError(getErrorMessage(err) || 'Failed to run vendor census.');
      setResult(null);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="border rounded p-2 space-y-2" data-testid="autopsy-vendor-census">
      <div className="flex items-center gap-2">
        <Input
          value={vendor}
          onChange={(e) => setVendor(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') run(); }}
          placeholder="Vendor name (e.g. GEMELLYS)"
          className="h-8 text-xs"
          data-testid="autopsy-vendor-census-input"
        />
        <Button
          variant="outline" size="sm" onClick={run} disabled={running || !vendor.trim()}
          data-testid="autopsy-vendor-census-run"
        >
          {running ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Search className="h-3 w-3 mr-1" />}
          Run census
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground">
        Read-only, synchronous. For every no-candidate/ambiguous receipt from this vendor, scans every entity's bank
        feed for a plausible transaction nearby and reports found_in_window / found_outside_window (with the day
        gap) / not_in_feed_at_all -- the measured answer to why the true transaction misses the matcher's window.
      </p>
      {error && <p className="text-xs text-destructive" data-testid="autopsy-vendor-census-error">{error}</p>}
      {result && (
        <div className="space-y-1" data-testid="autopsy-vendor-census-result">
          <p className="text-xs text-muted-foreground">
            {result.vendor_normalized}: {result.receipt_count} receipt(s)
          </p>
          {result.results.map((r) => (
            <div
              key={r.invoice_id}
              className="text-xs font-mono border-t pt-1"
              data-testid={`autopsy-vendor-census-row-${r.invoice_id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span>{r.invoice_id}</span>
                <span className="flex items-center gap-2">
                  {r.outcome === 'found_outside_window' && (
                    <span className="text-muted-foreground">{r.days_outside_census_window}d outside window</span>
                  )}
                  {r.outcome === 'not_in_feed_at_all' && (
                    <span className="text-muted-foreground">radius {r.searched_days_radius}d</span>
                  )}
                  <Badge variant={censusOutcomeVariant(r.outcome)} className="text-[10px]">
                    {censusOutcomeLabel(r.outcome)}
                  </Badge>
                </span>
              </div>
              {r.outcome === 'found_in_window' && (
                <div
                  className="text-muted-foreground"
                  data-testid={`autopsy-vendor-census-row-detail-${r.invoice_id}`}
                >
                  tx {r.transaction_id} &middot; {r.amount} {r.currency} &middot; {r.date} &middot;
                  {' '}day gap {r.day_gap ?? '?'}
                </div>
              )}
            </div>
          ))}
          {result.results.length === 0 && (
            <p className="text-xs text-muted-foreground">No no-candidate/ambiguous receipts for this vendor.</p>
          )}
        </div>
      )}
    </div>
  );
}

// Mirrors services/receipt_paid_no_candidate_autopsy.py's ALL_KILL_CAUSES /
// KILL_* constants exactly -- never a second, independently-worded copy of
// what each cause means.
const KILL_CAUSE_LABELS = {
  missing_signal: 'Missing search signal',
  empty_pool: 'Empty pool (nothing nearby at all)',
  feed_coverage_gap: 'Window predates feed coverage',
  window: 'Outside date window',
  amount_fx: 'Amount / FX tolerance',
  vendor: 'Vendor match',
  ambiguous: 'Ambiguous (multiple candidates)',
  vendor_text_amount_mismatch: 'Vendor text anchored, amount mismatch',
  stale_reason_now_resolvable: 'Stale reason (now resolvable)',
  already_reconciled_externally: 'Already reconciled externally',
  settled_via_rent: 'Settled via rent',
  evaluation_timed_out: 'Evaluation timed out',
  unclassified: 'Unclassified',
};

const RANKED_CAUSE_ORDER = [
  'empty_pool', 'window', 'amount_fx', 'vendor', 'ambiguous', 'vendor_text_amount_mismatch',
  'feed_coverage_gap', 'missing_signal', 'stale_reason_now_resolvable',
  'already_reconciled_externally', 'settled_via_rent', 'evaluation_timed_out', 'unclassified',
];

function causeLabel(cause) {
  return KILL_CAUSE_LABELS[cause] || cause;
}

function receiptById(result, id) {
  return (result.receipts || []).find((r) => r.invoice_id === id);
}

function nearMissLabel(record) {
  if (!record) return null;
  const d = record.detail || {};
  switch (record.kill_cause) {
    case 'window':
      return d.nearest_miss_days_outside_window != null
        ? `${d.nearest_miss_days_outside_window}d outside window (tx ${d.nearest_miss_transaction_id})`
        : 'no vendor-matching transaction nearby';
    case 'amount_fx':
      return d.nearest_miss_delta != null
        ? `delta ${d.nearest_miss_delta} (${d.currency_pair || ''})`
        : 'no same/cross-currency candidate in window';
    case 'vendor':
      return d.best_similarity != null
        ? `${(d.best_similarity * 100).toFixed(0)}% similar to "${d.best_similarity_tx_text}"`
        : null;
    case 'ambiguous':
      return `${d.exact_amount_candidates ?? 0} exact-amount of ${d.candidate_count ?? '?'} candidates`;
    case 'empty_pool':
      return `${d.count_in ?? 0} transactions in scan window`;
    case 'vendor_text_amount_mismatch':
      return d.amount_delta != null
        ? `delta ${d.amount_delta} (tx ${d.transaction_id}, day gap ${d.day_gap ?? '?'})`
        : null;
    default:
      return null;
  }
}

function buildSummaryText(result) {
  const lines = ['No-Candidate Autopsy -- ranked kill causes', ''];
  lines.push(`Population: ${result.true_population_count} (scanned ${result.scanned})`);
  lines.push('');
  lines.push('By kill cause:');
  RANKED_CAUSE_ORDER.forEach((cause) => {
    const count = result.kill_stage_counts?.[cause] ?? 0;
    if (count > 0) lines.push(`  ${causeLabel(cause)}: ${count}`);
  });
  lines.push('');
  lines.push('By vendor x kill cause (ranked by count):');
  (result.vendor_kill_breakdown || []).forEach((row) => {
    const nm = row.near_miss ? ` (near-miss median ${row.near_miss.median}, n=${row.near_miss.count})` : '';
    lines.push(`  ${row.vendor_raw_example || row.vendor_normalized || '(no vendor)'} -- ${causeLabel(row.kill_cause)}: ${row.count}${nm}`);
  });
  return lines.join('\n');
}

function VendorKillRow({ row, result }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="border rounded text-xs" data-testid={`autopsy-vendor-row-${row.vendor_normalized}-${row.kill_cause}`}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-2 p-2 text-left"
      >
        <span className="flex items-center gap-1">
          {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          <span className="font-medium">{row.vendor_raw_example || row.vendor_normalized || '(no vendor)'}</span>
          <Badge variant="outline" className="text-[10px]">{causeLabel(row.kill_cause)}</Badge>
        </span>
        <span className="flex items-center gap-2">
          {row.near_miss && (
            <span className="text-muted-foreground">
              median {row.near_miss.median} (n={row.near_miss.count})
            </span>
          )}
          <Badge variant={row.count > 1 ? 'destructive' : 'outline'} className="text-[10px]">{row.count}</Badge>
        </span>
      </button>
      {expanded && (
        <div className="border-t p-2 space-y-2" data-testid={`autopsy-vendor-row-detail-${row.vendor_normalized}-${row.kill_cause}`}>
          {row.receipt_ids.map((id) => {
            const record = receiptById(result, id);
            if (row.kill_cause === 'ambiguous') {
              const ambiguous = (result.ambiguous_receipts || []).find((a) => a.invoice_id === id);
              return (
                <div key={id} className="font-mono space-y-1" data-testid={`autopsy-ambiguous-detail-${id}`}>
                  <p>
                    {id}
                    {ambiguous && (
                      <Badge
                        variant={ambiguous.exact_amount_candidates === 1 ? 'default' : 'outline'}
                        className="text-[10px] ml-2"
                        data-testid={`autopsy-exact-amount-flag-${id}`}
                      >
                        exact_amount_candidates: {ambiguous.exact_amount_candidates}
                      </Badge>
                    )}
                  </p>
                  {ambiguous?.candidates?.length > 0 && (
                    <ul className="pl-4 space-y-0.5">
                      {ambiguous.candidates.map((c) => (
                        <li key={c.transaction_id} className={c.exact_amount_match ? 'text-emerald-700 dark:text-emerald-400' : 'text-muted-foreground'}>
                          tx {c.transaction_id} &middot; {c.amount} {c.currency} &middot; {c.date} &middot;
                          {' '}amount gap {c.amount_gap ?? '?'} &middot; day gap {c.day_gap ?? '?'}
                          {c.exact_amount_match && ' ✓ exact'}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            }
            return (
              <p key={id} className="font-mono text-muted-foreground">
                {id}{nearMissLabel(record) ? ` — ${nearMissLabel(record)}` : ''}
              </p>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function ReceiptPaidNoCandidateAutopsyPanel() {
  const [result, setResult] = useState(null);
  const [starting, setStarting] = useState(false);
  const [failed, setFailed] = useState(null);
  const [progress, setProgress] = useState(null);
  const [finishedAt, setFinishedAt] = useState(null);

  const poll = useJobPolling({
    fetchStatus: adminReceiptPaidNoCandidateAutopsyAPI.jobStatus,
    onCompleted: useCallback((jobResult) => {
      setResult(jobResult);
      setFailed(null);
      setProgress(null);
      setFinishedAt(new Date().toISOString());
    }, []),
    onFailed: useCallback((error) => {
      setFailed(error);
      setProgress(null);
    }, []),
    onProgress: useCallback((p) => {
      setProgress(p);
    }, []),
  });

  // Own resume effect (rather than useJobResume) -- that shared hook only
  // resumes polling for a still-RUNNING job; this card also wants to
  // re-render the last COMPLETED run's result after a page reload, which
  // needs the job doc itself (finished_at, result), not just a poll.start.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await adminReceiptPaidNoCandidateAutopsyAPI.jobLatest();
        const job = data.job;
        if (!job) return;
        if (job.status === 'running') {
          poll.start(job._id || job.id);
        } else if (job.status === 'completed' && job.result) {
          setResult(job.result);
          setFinishedAt(job.finished_at || null);
        }
      } catch {
        // Best-effort resume only -- a failed lookup here shouldn't block the panel.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async () => {
    setStarting(true);
    setFailed(null);
    setProgress(null);
    try {
      const { data } = await adminReceiptPaidNoCandidateAutopsyAPI.start();
      if (data.job_id) {
        poll.start(data.job_id);
      }
    } catch (err) {
      setFailed(getErrorMessage(err) || 'Failed to start autopsy.');
    } finally {
      setStarting(false);
    }
  };

  const busy = starting || poll.polling;

  const copySummary = () => {
    if (!result) return;
    const text = buildSummaryText(result);
    navigator.clipboard.writeText(text)
      .then(() => toast.success('Copied ranked cause summary'))
      .catch((err) => toast.error('Could not copy to clipboard: ' + getErrorMessage(err)));
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <SearchCode className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">No-Candidate Autopsy</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Read-only. Batch-traces every receipt already stamped "no candidate found" or "ambiguous" (services/
          receipt_paid_no_candidate_autopsy.py), reusing the real matcher's own trace/candidate/resolver functions by
          reference, and ranks the causes so each can become its own fix slice with a measured row-drain.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={run} disabled={busy} data-testid="autopsy-run">
              {busy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Run Autopsy
            </Button>
            {finishedAt && (
              <span className="text-xs text-muted-foreground" data-testid="autopsy-last-run">
                Last run: {new Date(finishedAt).toLocaleString()}
              </span>
            )}
          </div>
          <Button
            variant="ghost" size="sm" onClick={copySummary} disabled={!result}
            data-testid="autopsy-copy-summary"
          >
            <ClipboardCopy className="h-3 w-3 mr-1" /> Copy summary
          </Button>
        </div>

        {poll.polling && (
          <JobProgressLine progress={progress} testId="autopsy-progress">
            {(p) => <>Scanned {p.scanned ?? 0} of {p.total ?? '?'} ({p.seen ?? 0} in population)</>}
          </JobProgressLine>
        )}

        <JobErrorBlock error={failed} testId="autopsy-error" />

        <VendorCensusPanel />

        {result && (
          <div className="space-y-3" data-testid="autopsy-result">
            <div className="flex flex-wrap gap-2 text-xs">
              <Badge variant="outline" className="text-[10px]">Population: {result.true_population_count}</Badge>
              <Badge variant="outline" className="text-[10px]">Scanned: {result.scanned}</Badge>
              {result.population_truncated && (
                <Badge variant="destructive" className="text-[10px]">Truncated</Badge>
              )}
              {result.skipped_receipts?.count > 0 && (
                <Badge variant="destructive" className="text-[10px]">Skipped: {result.skipped_receipts.count}</Badge>
              )}
            </div>

            <div className="flex flex-wrap gap-1.5" data-testid="autopsy-kill-stage-chips">
              {RANKED_CAUSE_ORDER.filter((c) => (result.kill_stage_counts?.[c] ?? 0) > 0).map((cause) => (
                <Badge
                  key={cause} variant={cause === 'ambiguous' || cause === 'empty_pool' || cause === 'window' ? 'destructive' : 'outline'}
                  className="text-[10px]" data-testid={`autopsy-kill-chip-${cause}`}
                >
                  {causeLabel(cause)}: {result.kill_stage_counts[cause]}
                </Badge>
              ))}
            </div>

            <div className="space-y-1" data-testid="autopsy-vendor-kill-breakdown">
              {(result.vendor_kill_breakdown || []).map((row) => (
                <VendorKillRow key={`${row.vendor_normalized}-${row.kill_cause}`} row={row} result={result} />
              ))}
              {(result.vendor_kill_breakdown || []).length === 0 && (
                <p className="text-xs text-muted-foreground">Nothing in the no-candidate/ambiguous population.</p>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
